import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Film, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, formatApiError } from '@/lib/api';

const Field = ({ label, children, hint, error }) => (
  <div>
    <label className="text-[11px] tracking-widest uppercase font-mono text-white/60">{label}</label>
    <div className="mt-1.5">{children}</div>
    {error
      ? <div className="mt-1 text-[11px] text-red-400">{error}</div>
      : (hint && <div className="mt-1 text-[11px] text-white/40">{hint}</div>)}
  </div>
);

const Input = (props) => (
  <input {...props}
    className={`w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 focus:border-brand-cyan/60 focus:outline-none placeholder:text-white/30 ${props.className || ''}`} />
);

const Textarea = (props) => (
  <textarea {...props}
    className={`w-full px-3 py-2.5 text-sm rounded-lg bg-white/5 border border-white/10 focus:border-brand-cyan/60 focus:outline-none placeholder:text-white/30 resize-none ${props.className || ''}`} />
);

const SubmitButton = ({ submitting, children, ...rest }) => (
  <button type="submit" disabled={submitting} {...rest}
    className="bg-brand-cyan text-black font-semibold px-5 py-2.5 rounded-lg btn-glow hover:bg-brand-cyanSoft transition disabled:opacity-60">
    {submitting ? 'Saving...' : children}
  </button>
);

// ============== Course Modal ==============
export const CourseModal = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({
    course_name: '', slug: '', short_description: '', duration: '',
    price: '', razorpay_link: '', thumbnail_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.course_name || !form.slug) { toast.error('Course name and slug are required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        slug: form.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        price: form.price ? parseFloat(form.price) : null,
      };
      const { data } = await api.post('/admin/courses', payload);
      toast.success(`Course "${data.course_name}" created`);
      onCreated?.(data);
      onOpenChange(false);
      setForm({ course_name: '', slug: '', short_description: '', duration: '', price: '', razorpay_link: '', thumbnail_url: '' });
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1221] border-white/10 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Create new course</DialogTitle>
          <DialogDescription className="text-white/60">Add a new BYMAT Automation course to the catalog.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Course name"><Input data-testid="course-name-input" value={form.course_name} onChange={set('course_name')} placeholder="e.g., Selenium + Java + AI" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Slug (URL-safe)"><Input data-testid="course-slug-input" value={form.slug} onChange={set('slug')} placeholder="selenium-java-ai" /></Field>
            <Field label="Duration"><Input data-testid="course-duration-input" value={form.duration} onChange={set('duration')} placeholder="90 Days" /></Field>
          </div>
          <Field label="Short description"><Textarea rows={2} data-testid="course-shortdesc-input" value={form.short_description} onChange={set('short_description')} placeholder="One-line teaser shown on cards" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price (INR)"><Input type="number" data-testid="course-price-input" value={form.price} onChange={set('price')} placeholder="29999" /></Field>
            <Field label="Thumbnail URL"><Input data-testid="course-thumb-input" value={form.thumbnail_url} onChange={set('thumbnail_url')} placeholder="https://..." /></Field>
          </div>
          <Field label="Razorpay link"><Input data-testid="course-razorpay-input" value={form.razorpay_link} onChange={set('razorpay_link')} placeholder="https://rzp.io/..." /></Field>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
            <SubmitButton submitting={submitting} data-testid="course-submit-button">Create course</SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== Batch Modal ==============
const RAZORPAY_URL_RE = /^https:\/\/(?:rzp\.io|pages\.razorpay\.com|razorpay\.com)\/[A-Za-z0-9._/?&=#%+-]+$/;

const getBatchFormState = (initial) => initial ? {
  course_id: initial.course_id || '',
  batch_name: initial.batch_name || '',
  start_date: initial.start_date ? initial.start_date.slice(0, 10) : '',
  end_date: initial.end_date ? initial.end_date.slice(0, 10) : '',
  trainer_name: initial.trainer_name || '',
  meeting_link: initial.meeting_link || '',
  course_fee: initial.course_fee != null ? String(initial.course_fee) : '',
  payment_link: initial.payment_link || '',
} : {
  course_id: '', batch_name: '', start_date: '', end_date: '', trainer_name: '', meeting_link: '', course_fee: '', payment_link: '',
};

export const BatchModal = ({ open, onOpenChange, courses, onCreated, initial = null }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => getBatchFormState(initial));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(getBatchFormState(initial));
    setErrors({});
    setSubmitting(false);
  }, [open, initial]);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors((p) => { const n = { ...p }; delete n[k]; return n; });
  };

  const validate = () => {
    const errs = {};
    if (!isEdit && !form.course_id) errs.course_id = 'Select a course';
    if (!form.batch_name) errs.batch_name = 'Batch name is required';

    const feeStr = String(form.course_fee || '').trim();
    if (!feeStr) errs.course_fee = 'Course Fee is required';
    else if (!/^\d+(\.\d{1,2})?$/.test(feeStr)) errs.course_fee = 'Please enter a valid course fee';
    else {
      const v = parseFloat(feeStr);
      if (Number.isNaN(v) || v <= 0) errs.course_fee = 'Course Fee must be greater than 0';
      else if (v > 999999) errs.course_fee = 'Course Fee cannot exceed 999999';
    }

    const link = (form.payment_link || '').trim();
    if (link && !RAZORPAY_URL_RE.test(link)) errs.payment_link = 'Please enter a valid Razorpay payment URL';

    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(Object.values(errs)[0]); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.course_fee = parseFloat(form.course_fee);
      payload.payment_link = (form.payment_link || '').trim() || null;
      if (isEdit) delete payload.course_id;
      if (payload.start_date) payload.start_date = `${payload.start_date}T00:00:00`;
      if (payload.end_date) payload.end_date = `${payload.end_date}T00:00:00`;
      const { data } = isEdit
        ? await api.patch(`/admin/batches/${initial.id}`, payload)
        : await api.post('/admin/batches', payload);
      toast.success(`Batch ${isEdit ? 'updated' : 'created'}: ${data.batch_name}`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1221] border-white/10 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? 'Edit batch' : 'Create new batch'}</DialogTitle>
          <DialogDescription className="text-white/60">{isEdit ? 'Update batch details and schedule.' : 'Spin up a new cohort and assign a trainer.'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {!isEdit && (
            <Field label="Course" error={errors.course_id}>
              <Select value={form.course_id} onValueChange={(v) => { setForm({ ...form, course_id: v }); if (errors.course_id) setErrors((p) => { const n = { ...p }; delete n.course_id; return n; }); }}>
                <SelectTrigger data-testid="batch-course-select" className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Choose a course" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1221] border-white/10 text-white">
                  {(courses || []).map((c) => (
                    <SelectItem key={c.id} value={c.id} className="focus:bg-brand-cyan/10">{c.course_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Batch name" error={errors.batch_name}><Input data-testid="batch-name-input" value={form.batch_name} onChange={set('batch_name')} placeholder="Selenium AI — Batch S26" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><Input type="date" data-testid="batch-start-input" value={form.start_date} onChange={set('start_date')} /></Field>
            <Field label="End date"><Input type="date" data-testid="batch-end-input" value={form.end_date} onChange={set('end_date')} /></Field>
          </div>
          <Field label="Trainer name"><Input data-testid="batch-trainer-input" value={form.trainer_name} onChange={set('trainer_name')} placeholder="Rahul Ganjare" /></Field>
          <Field label="Meeting link"><Input data-testid="batch-meeting-input" value={form.meeting_link} onChange={set('meeting_link')} placeholder="https://meet.google.com/..." /></Field>

          {/* Course Fee — mandatory, numeric (decimals allowed), 1..999999 */}
          <Field label="Course Fee" error={errors.course_fee}>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 font-mono text-sm pointer-events-none">₹</span>
              <Input
                data-testid="batch-fee-input"
                type="number"
                inputMode="decimal"
                min="1"
                max="999999"
                step="0.01"
                value={form.course_fee}
                onChange={set('course_fee')}
                placeholder="Enter Course Fee"
                className="pl-7"
              />
            </div>
          </Field>

          {/* Payment Link — optional, must be Razorpay URL if provided */}
          <Field label="Payment Link" error={errors.payment_link}>
            <Input
              data-testid="batch-payment-link-input"
              type="url"
              value={form.payment_link}
              onChange={set('payment_link')}
              placeholder="https://rzp.io/rzp/xxxxxxxx"
            />
          </Field>

          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
            <SubmitButton submitting={submitting} data-testid="batch-submit-button">{isEdit ? 'Save changes' : 'Create batch'}</SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== Enrollment Modal ==============
export const EnrollmentModal = ({ open, onOpenChange, students, batches, onCreated }) => {
  const [form, setForm] = useState({ student_id: '', batch_id: '', payment_status: 'manual', access_status: 'active' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_id || !form.batch_id) { toast.error('Pick a student and a batch'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/admin/enrollments', form);
      toast.success('Student enrolled successfully');
      onCreated?.(data);
      onOpenChange(false);
      setForm({ student_id: '', batch_id: '', payment_status: 'manual', access_status: 'active' });
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1221] border-white/10 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Enroll student into a batch</DialogTitle>
          <DialogDescription className="text-white/60">Assign a registered student to an active batch with access controls.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Student">
            <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
              <SelectTrigger data-testid="enroll-student-select" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Pick a student" /></SelectTrigger>
              <SelectContent className="bg-[#0B1221] border-white/10 text-white max-h-72">
                {(students || []).map((s) => (
                  <SelectItem key={s.id} value={s.id} className="focus:bg-brand-cyan/10">{s.full_name} — {s.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Batch">
            <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
              <SelectTrigger data-testid="enroll-batch-select" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Pick a batch" /></SelectTrigger>
              <SelectContent className="bg-[#0B1221] border-white/10 text-white max-h-72">
                {(batches || []).map((b) => (
                  <SelectItem key={b.id} value={b.id} className="focus:bg-brand-cyan/10">{b.batch_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Payment status">
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B1221] border-white/10 text-white">
                  <SelectItem value="manual" className="focus:bg-brand-cyan/10">Manual</SelectItem>
                  <SelectItem value="paid" className="focus:bg-brand-cyan/10">Paid</SelectItem>
                  <SelectItem value="pending" className="focus:bg-brand-cyan/10">Pending</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Access status">
              <Select value={form.access_status} onValueChange={(v) => setForm({ ...form, access_status: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B1221] border-white/10 text-white">
                  <SelectItem value="active" className="focus:bg-brand-cyan/10">Active</SelectItem>
                  <SelectItem value="paused" className="focus:bg-brand-cyan/10">Paused</SelectItem>
                  <SelectItem value="revoked" className="focus:bg-brand-cyan/10">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
            <SubmitButton submitting={submitting} data-testid="enroll-submit-button">Enroll student</SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== Video Upload Modal (Bunny Stream) ==============
export const VideoUploadModal = ({ open, onOpenChange, batches, onUploaded }) => {
  const [title, setTitle] = useState('');
  const [batchId, setBatchId] = useState('');
  const [allowDownload, setAllowDownload] = useState(false);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  const reset = () => { setTitle(''); setBatchId(''); setAllowDownload(false); setFile(null); setProgress(0); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !batchId || !file) { toast.error('Title, batch and video file are required'); return; }
    if (!file.type.startsWith('video/')) { toast.error('Please choose a video file (MP4 preferred)'); return; }
    const batch = batches.find((b) => b.id === batchId);
    const fd = new FormData();
    fd.append('title', title);
    fd.append('batch_id', batchId);
    if (batch?.course_id) fd.append('course_id', batch.course_id);
    fd.append('allow_download', allowDownload ? 'true' : 'false');
    fd.append('file', file);
    setSubmitting(true); setProgress(0);
    try {
      await api.post('/admin/videos/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
        timeout: 0,
      });
      toast.success(`Uploaded "${title}" to Bunny Stream`);
      onUploaded?.();
      reset();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent className="bg-[#0B1221] border-white/10 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Film className="w-5 h-5 text-brand-cyan" /> Upload session recording</DialogTitle>
          <DialogDescription className="text-white/60">Video is streamed directly to Bunny Stream. MP4 recommended. Large files may take several minutes.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Title"><Input data-testid="video-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Session 01 — Selenium Architecture" /></Field>
          <Field label="Batch">
            <Select value={batchId} onValueChange={setBatchId}>
              <SelectTrigger data-testid="video-batch-select" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Pick a batch" /></SelectTrigger>
              <SelectContent className="bg-[#0B1221] border-white/10 text-white max-h-72">
                {(batches || []).map((b) => (
                  <SelectItem key={b.id} value={b.id} className="focus:bg-brand-cyan/10">{b.batch_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Video file">
            <label className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:border-brand-cyan/40">
              <span className="text-sm text-white/80 truncate">{file ? `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)` : 'Click to choose an MP4 file'}</span>
              <Upload className="w-4 h-4 text-brand-cyan flex-shrink-0" />
              <input type="file" accept="video/*" data-testid="video-file-input" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </Field>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" className="accent-cyan-400" checked={allowDownload} onChange={(e) => setAllowDownload(e.target.checked)} />
            Allow students to download this recording
          </label>
          {submitting && (
            <div>
              <div className="text-xs text-white/60 mb-1.5 font-mono">Uploading... {progress}%</div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-brand-cyan transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} disabled={submitting} className="px-4 py-2 text-sm text-white/60 hover:text-white disabled:opacity-50">Cancel</button>
            <SubmitButton submitting={submitting} data-testid="video-upload-submit">Upload video</SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== Resource Upload Modal (Cloudflare R2) ==============
export const ResourceUploadModal = ({ open, onOpenChange, batches, onUploaded }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [batchId, setBatchId] = useState('');
  const [isDownloadable, setIsDownloadable] = useState(true);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  const reset = () => { setTitle(''); setDescription(''); setBatchId(''); setIsDownloadable(true); setFile(null); setProgress(0); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !batchId || !file) { toast.error('Title, batch and file are required'); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error('File too large (max 100MB)'); return; }
    const batch = batches.find((b) => b.id === batchId);
    const fd = new FormData();
    fd.append('title', title);
    if (description) fd.append('description', description);
    fd.append('batch_id', batchId);
    if (batch?.course_id) fd.append('course_id', batch.course_id);
    fd.append('is_downloadable', isDownloadable ? 'true' : 'false');
    fd.append('file', file);
    setSubmitting(true); setProgress(0);
    try {
      await api.post('/admin/resources/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
        timeout: 0,
      });
      toast.success(`Uploaded "${title}" to R2`);
      onUploaded?.();
      reset();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent className="bg-[#0B1221] border-white/10 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><FileText className="w-5 h-5 text-brand-cyan" /> Upload downloadable resource</DialogTitle>
          <DialogDescription className="text-white/60">Stored in Cloudflare R2. Students get short-lived signed URLs (5-min expiry). Max 100 MB per file.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Title"><Input data-testid="resource-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Selenium Framework Source Code" /></Field>
          <Field label="Description (optional)"><Textarea rows={2} data-testid="resource-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="A short context shown to students" /></Field>
          <Field label="Batch">
            <Select value={batchId} onValueChange={setBatchId}>
              <SelectTrigger data-testid="resource-batch-select" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Pick a batch" /></SelectTrigger>
              <SelectContent className="bg-[#0B1221] border-white/10 text-white max-h-72">
                {(batches || []).map((b) => (
                  <SelectItem key={b.id} value={b.id} className="focus:bg-brand-cyan/10">{b.batch_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="File">
            <label className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:border-brand-cyan/40">
              <span className="text-sm text-white/80 truncate">{file ? `${file.name} (${(file.size / 1024).toFixed(1)} KB)` : 'Click to choose a file (PDF / DOCX / ZIP / image)'}</span>
              <Upload className="w-4 h-4 text-brand-cyan flex-shrink-0" />
              <input type="file" data-testid="resource-file-input" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </Field>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" className="accent-cyan-400" checked={isDownloadable} onChange={(e) => setIsDownloadable(e.target.checked)} />
            Allow students to download
          </label>
          {submitting && (
            <div>
              <div className="text-xs text-white/60 mb-1.5 font-mono">Uploading... {progress}%</div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-brand-cyan transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} disabled={submitting} className="px-4 py-2 text-sm text-white/60 hover:text-white disabled:opacity-50">Cancel</button>
            <SubmitButton submitting={submitting} data-testid="resource-upload-submit">Upload file</SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
